import { Document } from "@/common/apis/dtos";
import { useEmailDocuments } from "@/common/apis/notifications-mutation";
import { MultipleEmailInput } from "@/common/components/multiple-email-input/MultipleEmailInput";
import { useAnalytics } from '@/common/hooks/useAnalytics';
import { usePerformanceTimer } from "@/common/hooks/usePerformanceTimer";
import { getDocumentTypeDisplayName } from "@/common/utilities/display-utilities";
import { Banner, TextArea, Typography, UnstableSideSheet, UnstableDocumentCard, Button } from "@chr/eds-react";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { EmailDocumentSuccess } from "./EmailDocumentSuccess";

export interface EmailDocumentModalProps {
  open: boolean;
  handleClose: (event: object) => void
  documents: Document[];
}

export const EmailDocumentModal = ({ open, handleClose, documents }: EmailDocumentModalProps) => {
  const translate = useTranslation();
  const { isLoading, isSuccess, isError, mutate: sendEmail, reset } = useEmailDocuments();
  const [emailToValues, setEmailToValues] = useState<string[]>([]);
  const [messageValue, setMessageValue] = useState('');
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const { trackDocActivity } = useAnalytics();

  // Measure the performance of the email sending activity
  const [elapsedTime] = usePerformanceTimer(isLoading, isSuccess);

  useEffect(() => {
    if (isError) {
      setShowErrorBanner(true);
    }
  }, [isError, setShowErrorBanner]);

  const onClose = (event: object) => {
    resetForm();
    handleClose(event);
  }

  const resetForm = () => {
    setEmailToValues([]);
    setMessageValue('');
    setShowErrorBanner(false);
    reset();
  }

  useEffect(() => {
    isSuccess && trackDocActivity({
      numberOfDocuments: documents.length,
      typeOfDocument: documents?.map(doc => doc.description).join(', '),
      buttonText: 'Send',
      activityResult: 'Email',
      activityResponseTime: elapsedTime,
    });
  }, [elapsedTime]);

  if (isSuccess) {
    return (
      <UnstableSideSheet
        open={open}
        onClose={onClose}
        title={translate('Share documents by email')}
        content={<EmailDocumentSuccess {...{ emailToValues, messageValue, documents, onAgain: resetForm }} />}
        primaryAction={{
          buttonText: translate('Close'),
          onClick: onClose,
        }}
      />
    );
  }

  return (
    <UnstableSideSheet
      open={open}
      onClose={onClose}
      title={translate('Share documents by email')}
      content={
        <Stack spacing={4}>
          <Stack marginTop={4} spacing={4}>
            {showErrorBanner &&
              <Banner
                onClose={() => {
                  setShowErrorBanner(false)
                }}
                title={translate('Email could not be sent.')}
                description={translate('Please try again later.')}
                severity="negative"
              />}
            <Typography variant="body100">
              {translate('Share access to booking documents via email. The email will come from Navisphere2.0@chrobinson.com')}
            </Typography>
            <MultipleEmailInput
              label={translate('Email To')}
              emails={emailToValues}
              onChange={setEmailToValues}
              fullWidth
            />
            <TextArea
              id="email-message-textarea"
              label={translate('Message')}
              maxLength={800}
              helperText={messageValue.length >= 800
                ? translate('Maximum of 800 characters reached.')
                : `${messageValue.length}/800`}
              value={messageValue}
              onChange={(e) => {
                setMessageValue(e.target.value);
              }}
            />
            <Stack spacing={1}>
              <Typography variant="subtitle200">
                {translate('Attachment')}
              </Typography>
              {
                documents.map(document => (
                  <UnstableDocumentCard 
                    key={document.id}
                    labelText={`${document.name} - ${translate(getDocumentTypeDisplayName(document.type))}`}
                  />
                ))
              }
            </Stack>
          </Stack>
          
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="space-between"
            sx={{
              '& .MuiButton-root': {
                textTransform: 'none',
              }
            }}
          >
            <Button
              data-testid="send-email-button"
              variant="primary"
              buttonText={translate('Send')}
              loading={isLoading}
              disabled={emailToValues.length < 1 || isLoading}
              onClick={() => {
                sendEmail({ emailTos: emailToValues, message: messageValue, documents });
              }}
              fullWidth
            />
            <Button
              data-testid="cancel-email-button"
              variant="secondary"
              buttonText={translate('Cancel')}
              disabled={isLoading}
              onClick={onClose}
              fullWidth
            />
          </Stack>
        </Stack>
      }
    />
  );
}
